import { useState, useEffect } from 'react';
import {
  onSnapshot,
  collection,
  query,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { SettlementTransaction } from '@/lib/types';
import * as SettlementService from '@/services/settlement.service';

export const deriveSettlementView = SettlementService.deriveSettlementView;

export async function updateCashReceived(
  gameId: string,
  playerUserId: string,
  cash_received: number
): Promise<void> {
  return SettlementService.updateCashReceived(gameId, playerUserId, cash_received);
}

export async function updateAllCashReceived(
  gameId: string,
  cashUpdates: Record<string, number>
): Promise<void> {
  return SettlementService.updateAllCashReceived(gameId, cashUpdates);
}

export async function finalizeChipCounts(
  gameId: string,
  adminUserId: string,
  final_stacks: Record<string, number>,
  forceSettleConfirmed: boolean = false,
): Promise<void> {
  return SettlementService.finalizeChipCounts(gameId, adminUserId, final_stacks, forceSettleConfirmed);
}

export async function finalizeCashDistribution(
  gameId: string,
  adminUserId: string,
  cash_received: Record<string, number>
): Promise<void> {
  return SettlementService.finalizeCashDistribution(gameId, adminUserId, cash_received);
}

export async function postDigitalTransfers(
  gameId: string,
  adminUserId: string
): Promise<void> {
  return SettlementService.postDigitalTransfers(gameId, adminUserId);
}

export async function confirmDigitalTransfers(
  gameId: string,
  adminUserId: string
): Promise<void> {
  return SettlementService.confirmDigitalTransfers(gameId, adminUserId);
}

export async function calculateSettlement(
  gameId: string,
  adminUserId: string
): Promise<string> {
  return SettlementService.calculateSettlement(gameId, adminUserId);
}

export async function toggleTransactionSettled(transactionId: string, userId: string, isCurrentlySettled: boolean): Promise<void> {
  return SettlementService.toggleTransactionSettled(transactionId, userId, isCurrentlySettled);
}

export async function markAllTransactionsSettled(gameId: string, adminUserId: string): Promise<void> {
  return SettlementService.markAllTransactionsSettled(gameId, adminUserId);
}

export async function settleAllTransactions(gameId: string, adminUserId: string): Promise<void> {
  return SettlementService.settleAllTransactions(gameId, adminUserId);
}

interface UseSettlementTransactionsReturn {
  transactions: SettlementTransaction[];
  loading: boolean;
}

export function useSettlementTransactions(gameId?: string): UseSettlementTransactionsReturn {
  const [transactions, setTransactions] = useState<SettlementTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gameId) { setTransactions([]); setLoading(false); return; }
    const unsub = onSnapshot(
      query(collection(db, 'settlement_transactions'), where('game_id', '==', gameId)),
      (snap) => {
        setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() }) as SettlementTransaction));
        setLoading(false);
      }
    );
    return unsub;
  }, [gameId]);

  return { transactions, loading };
}

export async function migrateLegacySettlement(gameId: string): Promise<void> {
  return SettlementService.migrateLegacySettlement(gameId);
}

export async function reopenGame(gameId: string, userId: string): Promise<void> {
  return SettlementService.reopenGame(gameId, userId);
}

export async function resetSettlement(gameId: string): Promise<void> {
  return SettlementService.resetSettlement(gameId);
}

export async function unlockStep(
  gameId: string,
  adminUserId: string,
  targetView: 'chip_counts' | 'cash_distribution' | 'digital_transfers'
): Promise<void> {
  return SettlementService.unlockStep(gameId, adminUserId, targetView);
}

